import { deleteCallback, getCallback } from '@/utils/auth/callbacks';

import { CUSTOM_ERROR_MESSAGES } from '@/utils/auth/helpers';

import { switchAuthFlowType } from '@/utils/aws/cognito/config';

import {
  requestPasswordReset,
  signInWithPassword,
} from '@/utils/aws/cognito/amplifyAuth';

import { useRouter } from 'next/router';

import { useState } from 'react';

export default function useLogin(formData, updateSnack) {
  const router = useRouter();

  const [loading, setLoading] = useState(false);

  const [loadingStep, setLoadingStep] = useState(null);

  const [mfaUser, setMfaUser] = useState(null);

  const [cognitoResponse, setCognitoResponse] = useState(null);

  const [finalizingNewPassword, setFinalizingNewPassword] = useState(false);

  const [step, setStep] = useState('login');

  const handleLoginSubmit = async () => {
    setLoadingStep('login');

    setLoading(true);

    try {
      const trimEmail = formData.email.trim();

      const trimPassword = formData.password.trim();

      const user = await signInWithPassword(trimEmail, trimPassword);

      // Check for new password requirement

      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // The user object was created under CUSTOM_AUTH, which has wrong callbacks

        // We need to create a new user object under USER_SRP_AUTH for completeNewPassword to work

        // Switch to USER_SRP_AUTH

        switchAuthFlowType('USER_SRP_AUTH');

        try {
          // Re-sign-in with the same credentials under SRP to get a user with SRP callbacks

          const srpUser = await signInWithPassword(trimEmail, trimPassword);

          if (srpUser.challengeName !== 'NEW_PASSWORD_REQUIRED') {
            throw new Error(
              `Expected NEW_PASSWORD_REQUIRED under SRP but got ${srpUser.challengeName}`
            );
          }

          // Store this SRP-based user (has correct callbacks for completeNewPassword)

          setCognitoResponse(srpUser);

          setFinalizingNewPassword(true);

          setLoadingStep(null);

          setLoading(false);

          return { requiresNewPassword: true };
        } finally {
          // Always switch back to CUSTOM_AUTH for future logins

          switchAuthFlowType('CUSTOM_AUTH');
        }
      }

      // Check if MFA is required (CUSTOM_CHALLENGE)

      if (user.challengeName === 'CUSTOM_CHALLENGE') {
        // All MFA method information comes from backend Lambda response in challengeParam

        // No need to call unprotected routes - backend provides everything securely

        const preferredMethod = user.challengeParam?.preferredMethod;

        // Backend returns availableMethods as comma-separated string, convert to array

        const availableMethodsStr =
          user.challengeParam?.availableMethods || 'email';

        const availableMethods =
          typeof availableMethodsStr === 'string'
            ? availableMethodsStr.split(',').filter(Boolean)
            : Array.isArray(availableMethodsStr)
            ? availableMethodsStr
            : ['email'];

        // Use hasPhone from backend response, or derive from availableMethods

        const hasPhoneFromBackend =
          user.challengeParam?.hasPhone === 'true' ||
          user.challengeParam?.hasPhone === true;

        const hasPhone =
          hasPhoneFromBackend || availableMethods.includes('sms');

        // Validate preferred method is available

        const hasValidPreferredMethod =
          preferredMethod &&
          availableMethods.includes(preferredMethod) &&
          (preferredMethod !== 'sms' || hasPhone);

        setMfaUser(user);

        // If preferred method exists and is available, skip method selection

        // useMfaFlow will auto-send OTP to preferred method

        setStep(hasValidPreferredMethod ? 'mfa-otp' : 'mfa-method');

        setLoadingStep(null);

        setLoading(false);

        return { requiresMfa: true };
      }

      // Normal login flow (no MFA required) - tokens issued

      const callbackPath = getCallback();

      deleteCallback();

      if (callbackPath) {
        router.push(callbackPath);
      } else {
        router.push('/hub');
      }

      return { success: true };
    } catch (error) {
      setLoadingStep(null);

      setFinalizingNewPassword(false);

      if (error.code === 'PasswordResetRequiredException') {
        const trimEmail = formData.email.trim();

        // Switch to USER_SRP_AUTH for forgotPassword

        switchAuthFlowType('USER_SRP_AUTH');

        try {
          await requestPasswordReset(trimEmail);

          router.push({
            pathname: '/confirm-password-reset',

            query: { email: trimEmail },
          });
        } finally {
          // Always switch back to CUSTOM_AUTH for future logins

          switchAuthFlowType('CUSTOM_AUTH');
        }

        return { passwordResetRequired: true };
      }

      setLoading(false);

      if (error.message && CUSTOM_ERROR_MESSAGES[error.message]) {
        updateSnack({
          severity: 'error',

          message: CUSTOM_ERROR_MESSAGES[error.message],
        });
      } else {
        updateSnack({
          severity: 'error',

          message: `Failed to login: ${error.message}`,
        });
      }

      return { error };
    }
  };

  return {
    loading,

    loadingStep,

    mfaUser,

    setMfaUser,

    cognitoResponse,

    finalizingNewPassword,

    setFinalizingNewPassword,

    step,

    setStep,

    handleLoginSubmit,
  };
}
